import React, { useContext, useMemo, useState } from 'react'

import { Box, Chip, ClassNameMap, Divider, fade, IconButton, makeAppStyles, ProviderLogo } from '@percept/mui'

import { ArrowDropDown, ArrowDropUp, AsteriskIcon, Home, Settings } from '@percept/mui/icons'

import { Link, useLocation } from 'react-router-dom'

import { Location } from 'history'

import { AppSettings } from 'components/AppSettings'

import { discoverStructuralReportProviders, useActiveOrgEnvironment } from 'components/Organisation'

import { get, some, uniq, xor } from 'lodash-es'

import { LocationLike, OrgUnitReportAvailabilityContext, UserPrivilegeContext } from '@percept/app-components'

import { isAdminUser, userHasOrgPrivileges } from '@percept/utils'

import { providerLabelMap, providerURLMap } from '@percept/constants'

import { PlatformUnit, ReportProviderV2, ReportSeries, UserPrivileges } from '@percept/types'


type LinkHierarchyClassName = (
  | 'root'
  | 'active'
  | 'route'
  | 'link'
  | 'linkDivider'
  | 'chipContent'
  | 'chipContentActive'
  | 'chipIcon'
)

type LinkHierarchyClasses = ClassNameMap<LinkHierarchyClassName>

type LinkHierarchy = {
  key: string
  route?: string | null
  label: React.ReactNode
  children?: LinkHierarchy[]
  active?: boolean
  disabled?: boolean
  type?: never
  chipContent?: React.ReactNode
  chipIcon?: JSX.Element

} | {
  key?: never
  route?: never
  label?: never
  active?: never
  disabled?: never
  chipContent?: never
  chipIcon?: never
  type: 'DIVIDER'
}

type LinkHierarchyProps = {
  currentLocation: Location
  linkItem: LinkHierarchy
  activeKeys: string[]
  toggleLinkKey: (key: string) => void
  onNavigate: (linkItem: LinkHierarchy) => void
  classes: LinkHierarchyClasses
  paddingLeft?: number
  marginTop?: number
  depth?: number
}

const LinkHierarchy = ({
  currentLocation,
  linkItem,
  activeKeys,
  toggleLinkKey,
  onNavigate,
  classes,
  depth = 0,
  paddingLeft = 8,
  marginTop = 1,
}: LinkHierarchyProps): JSX.Element => {
  if( linkItem.type === 'DIVIDER' ){
    return <Divider className={classes.linkDivider} />
  }
  let routeClassName = classes.root
  let chipClassName = classes.chipContent
  if( linkItem.route && !linkItem.disabled ){
    routeClassName += (' ' + classes.route)
  }
  const isActiveRoute = !!(linkItem.route && currentLocation.pathname === linkItem.route)
  if( isActiveRoute ){
    routeClassName += (' ' + classes.active)
    chipClassName += (' ' + classes.chipContentActive)
  }
  return (
    <>
    {/* <Box
      fontSize={14}
      fontWeight={500}
      my={2.5}
      marginTop={marginTop}
      paddingLeft={paddingLeft}> */}
      <div
        className={routeClassName}
        onClick={(): void => {
          onNavigate(linkItem)
        }}
        style={{
          paddingLeft: (depth * paddingLeft) + paddingLeft,
        }}>
        { linkItem.route && !linkItem.disabled ? (
          <Link className={classes.link} to={linkItem.route}>
            { linkItem.label }
            { !!linkItem.chipContent && (
              <Chip
                classes={{root: chipClassName, icon: classes.chipIcon}}
                size='small'
                variant='outlined'
                label={linkItem.chipContent}
                icon={linkItem.chipIcon} />
            )}
          </Link>
        ) : linkItem.label }
        {/* { linkItem.children && (
          <IconButton
            color='inherit'
            onClick={(): void => toggleLinkKey(linkItem.key)}>
            { isActive ? <ArrowDropUp /> : <ArrowDropDown /> }
          </IconButton>
        )} */}
      </div>
      { linkItem.children && linkItem.children.map( linkItemChild => (
        <LinkHierarchy
          key={linkItemChild.key}
          currentLocation={currentLocation}
          linkItem={linkItemChild}
          activeKeys={activeKeys}
          toggleLinkKey={toggleLinkKey}
          onNavigate={onNavigate}
          classes={classes}
          paddingLeft={paddingLeft}
          marginTop={1}
          depth={depth + 1} />
      )) }
    </>
  )
}


const useLinkHierarchy = (
  orgUnit: PlatformUnit | null,
  userPrivileges: UserPrivileges
): LinkHierarchy[] => {

  const reportingAvailability = useContext(OrgUnitReportAvailabilityContext)

  return useMemo<LinkHierarchy[]>(() => {
    const orgUnitId = get(orgUnit, 'id', null)

    const reportSeriesMap: Partial<Record<ReportProviderV2, ReportSeries>> = {}

    const providersWithAvailableReports: ReportProviderV2[] | null = (
      reportingAvailability && uniq(
        (reportingAvailability || []).map( a => a.provider )
      )
    )

    const hasReportsAvailable = providersWithAvailableReports && !!providersWithAvailableReports.length

    if( orgUnit && orgUnit.report_series ){
      for( const series of orgUnit.report_series ){
        reportSeriesMap[series.provider.slug] = series
      }
    }

    const linkHierarchy: LinkHierarchy[] = [
      {
        key: 'activation',
        label: 'Activation',
        children: [
          {
            key: 'campaign-setup',
            label: 'Campaign Setup',
            route: orgUnitId && `/dashboards/${orgUnitId}/campaign-setup`,
            disabled: hasReportsAvailable !== null ? !hasReportsAvailable : false,
            children: (
              discoverStructuralReportProviders(orgUnit).map(
                provider => {
                  const matchedSeries = reportSeriesMap[provider]
                  const route = (
                    matchedSeries ?
                      `/series/${matchedSeries.id}` :
                      orgUnitId && `/dashboards/${orgUnitId}/campaign-setup/${providerURLMap[provider]}`
                  )
                  return {
                    key: provider,
                    // label: <ProviderLogo provider={provider} size={1.35} style={{position: 'relative', top: 2}} />,
                    label: providerLabelMap[provider],
                    disabled: providersWithAvailableReports && !providersWithAvailableReports.includes(provider),
                    route,
                  }
                }
              )
            ) as LinkHierarchy[]
          },
          {
            key: 'performance',
            label: 'Performance',
            children: [
              {
                key: 'performance-data-view',
                label: 'Data Report',
                route: orgUnitId && `/dashboards/${orgUnitId}/data-warehouse`,
              },
              {
                key: 'performance-chart-view',
                label: 'Chart Builder',
                route: orgUnitId && `/dashboards/${orgUnitId}/chart-builder`,
                chipContent: 'Beta',
                chipIcon: <AsteriskIcon />,
              },
            ],
          },
        ]
      },
    ]

    if(
      isAdminUser(userPrivileges.user)
      || userHasOrgPrivileges(
        ['org.analytics.view'], orgUnitId, userPrivileges.org_privileges,
      )
    ){
      linkHierarchy.push({ type: 'DIVIDER' })
      linkHierarchy.push({
        key: 'user-analytics',
        label: 'User Analytics',
        route: orgUnitId && `/dashboards/${orgUnitId}/analytics`,
      })
    }

    return linkHierarchy
  }, [userPrivileges, orgUnit, reportingAvailability])
}


type DrawerClassName = (
  | 'drawer'
  | 'drawerHeader'
  | 'divider'
  | 'drawerFooter'
  | 'homeLink'
  | 'homeIcon'
)

const useStyles = makeAppStyles<{}, LinkHierarchyClassName | DrawerClassName>( theme => ({
  drawer: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '100%',
  },
  drawerHeader: {
    height: get(theme.overrides, ['MuiAppBar', 'root', 'height']),
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  drawerFooter: {
    marginTop: 'auto',
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${fade(theme.palette.text.primary, 0.1)}`,
  },
  homeLink: {
    display: 'flex',
    alignItems: 'center',
    flexBasis: '100%',
    backgroundColor: 'unset',
    fontSize: 14,
    fontWeight: theme.typography.fontWeightBold,
    transition: theme.transitions.create(['color', 'background-color']),
    '&$active': {
      backgroundColor: 'unset',
      color: theme.palette.primary.main,
    },
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&$active:hover': {
      backgroundColor: 'unset',
      color: theme.palette.primary.main,
    },
  },
  homeIcon: {
    marginRight: theme.spacing(0.75),
    fontSize: 18,
  },
  divider: {
    opacity: 0.5,
  },
  linkDivider: {
    opacity: 0.5,
    margin: theme.spacing(1.25, 0),
  },
  root: {
    display: 'flex',
    alignItems: 'center',
    userSelect: 'none',
    height: 36,
    fontSize: 14,
    [theme.breakpoints.between('md', 'lg')]: {
      height: 34,
      fontSize: 13,
    },
    fontWeight: theme.typography.fontWeightBold,
    transition: theme.transitions.create(['color', 'background-color']),
    color: theme.palette.text.secondary,
    backgroundColor: fade(theme.palette.primary.main, 0),
  },
  route: {
    color: theme.palette.text.primary,
    '&:not($active) :hover': {
      color: theme.palette.primary.main,
    },
  },
  active: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      color: theme.palette.primary.contrastText,
    },
  },
  link: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  chipContent: {
    color: 'inherit',
    border: `1px solid ${fade(theme.palette.text.primary, 0.5)}`,
    height: 16,
    padding: 0,
    fontSize: 10,
    marginLeft: 'auto',
    marginRight: 8,
  },
  chipContentActive: {
    border: `1px solid ${fade(theme.palette.primary.contrastText, 0.5)}`,
  },
  chipIcon: {
    fontSize: 12,
    width: 12,
    height: 12,
    color: 'inherit',
  },
  // selected: {
  //   backgroundColor: theme.palette.primary.main,
  //   color: theme.palette.primary.contrastText,
  // }
}))

export type DrawerContentProps = Pick<LinkHierarchyProps, 'onNavigate'>

export const DrawerContent = (props: DrawerContentProps): JSX.Element => {

  const userPrivileges = useContext(UserPrivilegeContext)

  const { activePlatformUnit } = useActiveOrgEnvironment()

  const linkHierarchy = useLinkHierarchy(activePlatformUnit, userPrivileges)

  const [activeKeys, setActiveKeys] = useState<string[]>([])

  const toggleLinkKey = (key: string): void => {
    setActiveKeys( prev => xor(prev, [key]) )
  }

  const { user } = userPrivileges

  const currentLocation = useLocation()
  
  const classes = useStyles()

  const orgUnitId = activePlatformUnit ? activePlatformUnit.id : user.default_org_unit_id

  const defaultRoute = `/dashboards/${orgUnitId}`
  let homeLinkClassName = classes.homeLink
  if( currentLocation.pathname === defaultRoute ){
    homeLinkClassName += (' ' + classes.active)
  }
  
  return (
    <div className={classes.drawer}>
      <div className={classes.drawerHeader}>
        <Link
          to={defaultRoute}
          className={homeLinkClassName}>
          <Home className={classes.homeIcon} color='inherit' />
          Home
        </Link>
        {/* <LinkHierarchy
          key='home-route'
          classes={classes}
          currentLocation={currentLocation}
          activeKeys={activeKeys}
          toggleLinkKey={toggleLinkKey}
          paddingLeft={16}
          {...props}
          linkItem={{
            key: 'home',
            route: `/dashboards/${user.default_org_unit_id}`,
            label: 'Home',
          }} /> */}
      </div>
      <Divider className={classes.divider} />
      { linkHierarchy.map( linkItem => (
        <LinkHierarchy
          key={linkItem.key}
          classes={classes}
          currentLocation={currentLocation}
          linkItem={linkItem}
          activeKeys={activeKeys}
          toggleLinkKey={toggleLinkKey}
          paddingLeft={12}
          {...props} />
      ))}
      <div className={classes.drawerFooter}>
        <AppSettings />
      </div>
      {/* <Box marginTop='auto'>
      </Box> */}
    </div>
  )
}
